// ============================================================
// PGN Parser — Task 1.1
// Parses PGN string using chess.js, extracts moves with FEN
// positions and game metadata.
// ============================================================

import { Chess } from "chess.js"
import type { GameMetadata, MoveData, PieceName } from "@shared/types"

// --- Internal helpers ---

const PIECE_MAP: Record<string, PieceName> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
}

function mapPiece(piece: string): PieceName {
  return PIECE_MAP[piece.toLowerCase()] ?? "pawn"
}

function parseResult(raw: string): GameMetadata["result"] {
  if (raw === "1-0" || raw === "0-1" || raw === "1/2-1/2") return raw
  return "*"
}

function extractElo(header?: string): number | null {
  if (header === undefined || header === null) return null
  const num = parseInt(header, 10)
  return Number.isNaN(num) ? null : num
}

/**
 * Resolve a human-readable opening name from PGN headers.
 * Priority: Opening header > ECOUrl slug > ECO code lookup > null
 */
function resolveOpening(header: Record<string, string | undefined>): string | null {
  // 1. Direct Opening header (Lichess, some others)
  if (header.Opening) return header.Opening

  // 2. Chess.com ECOUrl — extract slug and humanize it
  //    e.g. "https://www.chess.com/openings/French-Defense-Normal-Variation"
  if (header.ECOUrl) {
    const match = header.ECOUrl.match(/\/openings\/(.+?)(?:\?|$)/)
    if (match) {
      return match[1]
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    }
  }

  // 3. ECO code lookup (covers the main families)
  if (header.ECO) {
    return ECO_FAMILIES[header.ECO] ?? ecoFamilyFromLetter(header.ECO) ?? null
  }

  return null
}

/**
 * Specific ECO code → opening name mapping for the most common codes.
 * This isn't exhaustive — covers the codes players actually encounter.
 */
const ECO_FAMILIES: Record<string, string> = {
  // A-codes
  A00: "Uncommon Opening", A04: "Réti Opening", A05: "Réti Opening",
  A10: "English Opening", A13: "English Opening", A20: "English Opening",
  A30: "English Symmetrical", A40: "Queen's Pawn Game", A45: "Indian Game",
  A46: "Indian Game", A48: "London System", A50: "Indian Defense",
  A56: "Benoni Defense", A57: "Benko Gambit",
  // B-codes
  B00: "Uncommon King's Pawn", B01: "Scandinavian Defense",
  B02: "Alekhine's Defense", B06: "Modern Defense", B07: "Pirc Defense",
  B10: "Caro-Kann Defense", B12: "Caro-Kann Defense", B13: "Caro-Kann Defense",
  B20: "Sicilian Defense", B21: "Sicilian Smith-Morra", B23: "Sicilian Closed",
  B27: "Sicilian Defense", B30: "Sicilian Defense", B33: "Sicilian Sveshnikov",
  B40: "Sicilian Defense", B50: "Sicilian Defense", B60: "Sicilian Richter-Rauzer",
  B70: "Sicilian Dragon", B80: "Sicilian Scheveningen", B90: "Sicilian Najdorf",
  // C-codes
  C00: "French Defense", C01: "French Defense", C02: "French Advance",
  C03: "French Tarrasch", C10: "French Defense", C20: "King's Pawn Game",
  C21: "Center Game", C22: "Center Game", C23: "Bishop's Opening",
  C25: "Vienna Game", C30: "King's Gambit", C33: "King's Gambit Accepted",
  C40: "King's Knight Opening", C41: "Philidor Defense",
  C42: "Petrov's Defense", C44: "Scotch Game", C45: "Scotch Game",
  C46: "Three Knights Game", C47: "Four Knights Game",
  C50: "Italian Game", C53: "Italian Giuoco Piano", C54: "Italian Giuoco Piano",
  C55: "Two Knights Defense", C57: "Two Knights Traxler",
  C60: "Ruy López", C65: "Ruy López Berlin", C68: "Ruy López Exchange",
  C70: "Ruy López", C78: "Ruy López Morphy", C80: "Ruy López Open",
  C84: "Ruy López Closed", C88: "Ruy López Closed", C90: "Ruy López Closed",
  // D-codes
  D00: "Queen's Pawn Game", D02: "Queen's Pawn London",
  D06: "Queen's Gambit", D10: "Slav Defense", D15: "Slav Defense",
  D20: "Queen's Gambit Accepted", D30: "Queen's Gambit Declined",
  D35: "Queen's Gambit Declined", D37: "Queen's Gambit Declined",
  D43: "Semi-Slav Defense", D45: "Semi-Slav Defense",
  D70: "Grünfeld Defense", D80: "Grünfeld Defense", D85: "Grünfeld Defense",
  D90: "Grünfeld Defense", D92: "Grünfeld Defense",
  // E-codes
  E00: "Catalan Opening", E04: "Catalan Open", E06: "Catalan Closed",
  E10: "Queen's Pawn Game", E12: "Queen's Indian Defense",
  E15: "Queen's Indian Defense", E20: "Nimzo-Indian Defense",
  E32: "Nimzo-Indian Classical", E40: "Nimzo-Indian Defense",
  E60: "King's Indian Defense", E70: "King's Indian Defense",
  E76: "King's Indian Four Pawns", E80: "King's Indian Sämisch",
  E90: "King's Indian Defense", E97: "King's Indian Mar del Plata",
  E99: "King's Indian Mar del Plata",
}

/**
 * Fallback: derive a general family name from the ECO letter.
 * Only used when the specific code isn't in ECO_FAMILIES.
 */
function ecoFamilyFromLetter(eco: string): string | null {
  const letter = eco.charAt(0).toUpperCase()
  switch (letter) {
    case "A": return "Flank Opening"
    case "B": return "Semi-Open Game"
    case "C": return "Open Game"
    case "D": return "Closed Game"
    case "E": return "Indian Defense"
    default: return null
  }
}

// --- Public API ---

export interface ParsedGame {
  metadata: GameMetadata
  moves: Omit<MoveData, "eval" | "bestMove" | "evalDelta">[]
  startingFen: string
}

/**
 * Parse a PGN string into game metadata and move data.
 * Moves are returned WITHOUT eval data — that gets added by the Stockfish module.
 */
export function parsePgn(pgn: string): ParsedGame {
  const chess = new Chess()

  // Load the PGN — chess.js throws if invalid
  chess.loadPgn(pgn)

  // Extract headers
  const header = chess.getHeaders()
  const white = header.White ?? "Unknown"
  const black = header.Black ?? "Unknown"
  const result = parseResult(header.Result ?? "*")
  const timeControl = header.TimeControl ?? null
  const opening = resolveOpening(header as Record<string, string | undefined>)
  const date = header.Date ?? null
  const whiteElo = extractElo(header.WhiteElo)
  const blackElo = extractElo(header.BlackElo)

  // Get the full move history with details
  const history = chess.history({ verbose: true })

  // Build move list with FEN after each move
  // We need to replay the game to get FEN after each move
  chess.reset()

  // If the PGN had a FEN header (starting from a position), load it
  const fenHeader = header.FEN
  const startingFen =
    fenHeader ?? "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
  if (fenHeader) {
    chess.load(fenHeader)
  }

  const moves: Omit<MoveData, "eval" | "bestMove" | "evalDelta">[] = []

  for (let i = 0; i < history.length; i++) {
    const h = history[i]
    chess.move(h.san)

    const fen = chess.fen()
    const color: "white" | "black" = h.color === "w" ? "white" : "black"
    const moveNumber = Math.ceil((i + 1) / 2)

    moves.push({
      moveNumber,
      color,
      san: h.san,
      from: h.from,
      to: h.to,
      piece: mapPiece(h.piece),
      captured: h.captured ? mapPiece(h.captured) : null,
      promotion: h.promotion ? mapPiece(h.promotion) : null,
      fen,
    })
  }

  const totalMoves = Math.ceil(history.length / 2)

  const metadata: GameMetadata = {
    white,
    black,
    whiteElo,
    blackElo,
    result,
    timeControl,
    opening,
    totalMoves,
    date,
  }

  return { metadata, moves, startingFen }
}

/**
 * Normalize a PGN for deduplication hashing.
 * Strips headers, comments, annotations, move numbers.
 * Returns just the SAN moves separated by spaces.
 */
export function normalizePgnForHash(pgn: string): string {
  const chess = new Chess()
  chess.loadPgn(pgn)
  const history = chess.history()
  return history.join(" ")
}
